import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import styles from './ui-sidebar-item.module.scss';
import { NavLink, useMatch } from 'react-router';
import { UISpacer } from '../ui-spacer';
import { UIIcon } from '../ui-icon';

const ICON_SIZE = 19;

type UISidebarItemProps = {
    link?: string;
    externalLink?: string;
    onClick?: () => void;
    collapsed?: boolean;
    text: string;
    icon:
        | 'dashboard'
        | 'tasks'
        | 'teams'
        | 'reports'
        | 'settings'
        | 'store'
        | 'support'
        | 'collapse'
        | 'expand'
        | 'message-circle';
};

export function UISidebarItem(props: UISidebarItemProps): ReactElement {
    const {
        link,
        externalLink,
        onClick,
        collapsed = false,
        text,
        icon
    } = props;

    const _icon: ReactNode = useMemo(() => {
        switch (icon) {
            case 'dashboard':
                return <UIIcon name="activity" size={ICON_SIZE} />;
            case 'tasks':
                return <UIIcon name="clipboard" size={ICON_SIZE} />;
            case 'teams':
                return <UIIcon name="globe" size={ICON_SIZE} />;
            case 'reports':
                return <UIIcon name="bar-chart" size={ICON_SIZE} />;
            case 'settings':
                return <UIIcon name="settings" size={ICON_SIZE} />;
            case 'store':
                return <UIIcon name="shopping-cart" size={ICON_SIZE} />;
            case 'support':
                return <UIIcon name="help-circle" size={ICON_SIZE} />;
            case 'collapse':
                return <UIIcon name="chevron-left" size={ICON_SIZE} />;
            case 'expand':
                return <UIIcon name="chevron-right" size={ICON_SIZE} />;
            case 'message-circle':
                return <UIIcon name="message-circle" size={ICON_SIZE} />;
        }
    }, [icon]);

    const item = useCallback(() => {
        return (
            <>
                <div className={styles.iconContainer}>{_icon}</div>
                {!collapsed && (
                    <>
                        <UISpacer />
                        {text}
                    </>
                )}
            </>
        );
    }, [_icon, text, collapsed]);

    return (
        <div className={styles.container}>
            {link && <InternalLink link={link}>{item()}</InternalLink>}
            {externalLink && (
                <ExternalLink externalLink={externalLink}>
                    {item()}
                </ExternalLink>
            )}
            {onClick && <Button onClick={onClick}>{item()}</Button>}
        </div>
    );
}

function InternalLink(props: { link: string; children: ReactNode }) {
    const { link, children } = props;
    const match = useMatch(`${link}/*`);

    return (
        <>
            {match && <div className={styles.selectedIndicator} />}
            <NavLink
                to={link}
                className={({ isActive }) =>
                    isActive
                        ? `${styles.buttonContainer} ${styles.selected}`
                        : styles.buttonContainer
                }
            >
                {children}
            </NavLink>
        </>
    );
}

function ExternalLink(props: { externalLink: string; children: ReactNode }) {
    const { externalLink, children } = props;
    return (
        <a
            href={externalLink}
            target="_blank"
            rel="noreferrer"
            className={styles.buttonContainer}
        >
            {children}
        </a>
    );
}

function Button(props: { onClick: () => void; children: ReactNode }) {
    const { onClick, children } = props;
    return (
        <button onClick={onClick} className={styles.buttonContainer}>
            {children}
        </button>
    );
}
